import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const FORMAT_DATE = 'DD.MM.YYYY';
export const FORMAT_DATE_WITH_TIME = 'DD.MM.YYYY HH:mm';
export const FORMAT_DATE_WITH_TIME_SECONDS = 'DD.MM.YYYY HH:mm:ss';
export const FORMAT_TIME = 'HH:mm';
export const FORMAT_TIME_SECONDS = 'HH:mm:ss';
export const ISO_DATE_FORMAT_WITH_TIME_SECONDS = 'YYYY-MM-DDTHH:mm:ss';
export const ISO_DATE_FORMAT_WITH_TIME_MILLISECONDS = 'YYYY-MM-DDTHH:mm:ss.SSS';
export const DEFAULT_TZ = 'UTC';

/**
 * description Преобразование строки, содержащей дату в формате YYYY-MM-DDTHH:mm:ss в валидную дату dayjs в UTC
 */
export function convertToDayjs(
  date: string | number | dayjs.Dayjs | Date | null | undefined,
  format = ISO_DATE_FORMAT_WITH_TIME_SECONDS,
): dayjs.Dayjs {
  const isValid = date ? dayjs(date, format, false).isValid() : false;

  if (!isValid) {
    throw Error(
      `Invalid format of ${String(
        date,
      )}, should be ${ISO_DATE_FORMAT_WITH_TIME_SECONDS}`,
    );
  }

  return dayjs(date).utc(true);
}

/**
 * description Слияние дат: Дата (YYYY-MM-DD) из первой и время (HH:mm) из второй даты
 */
export const mergeDates = (
  value1: dayjs.Dayjs,
  value2: dayjs.Dayjs,
): dayjs.Dayjs => {
  return dayjs(value1)
    .set('hour', dayjs(value2).get('hour'))
    .set('minute', dayjs(value2).get('minute'))
    .set('second', 0);
};

export const formatDate = (
  date: string | number | dayjs.Dayjs | Date | null | undefined,
  format?: string,
): string => {
  if (!date) {
    throw Error(`Invalid date`);
  }

  const dd = ifDateConvert(date);
  if (dd) {
    return dd.format(format ?? FORMAT_DATE);
  }

  return String(date);
};

export const ifDateConvert = (
  date: string | number | dayjs.Dayjs | Date | null | undefined,
): dayjs.Dayjs | null => {
  try {
    return date ? convertToDayjs(date) : null;
  } catch {
    return null;
  }
};

/**
 * description Преобразование строки, содержащей дату в формате timestamp в строку нужного формата
 * (по умолчанию YYYY-MM-DDTHH:mm:ss)
 *
 */
export const timestampToFormattedDateString = (
  item: string,
  stringFormat?: string,
): string =>
  dayjs(new Date(parseInt(item)))
    .set('hour', 0)
    .set('minute', 0)
    .set('second', 0)
    .utc(true)
    ?.format(stringFormat || ISO_DATE_FORMAT_WITH_TIME_SECONDS);

/**
 * description Преобразование строки, содержащей дату в формате YYYY-MM-DDTHH:mm:ss в timestamp
 *
 */
export const formattedDateStringToTimestamp = (item: string): string =>
  (dayjs(item).utc(true).unix() * 1000).toString();

export const formattedDateStringWithZerosToTimestamp = (item: string): string =>
  (dayjs(item).hour(0).minute(0).second(0).utc(true).unix() * 1000).toString();

export const getFormattedDays = (days: string[]): string[] =>
  days?.map(day => convertToDayjs(day).format('YYYY-MM-DD')) ?? [];

/**
 * description Преобразование строки, содержащей время в формате HH:mm в секунды
 *
 */
export const getSecondsFromHoursAndMinutes = (time: string): number => {
  const pattern = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
  if (!time || !pattern.test(time)) {
    throw Error(`Invalid time`);
  }
  const [hours, minutes] = time.split(':');
  return +minutes * 60 + +hours * 60 * 60;
};

/**
 * description Возвращает дату и время в формате DD.MM.YYYY HH:mm:ss
 * (соответствует константе FORMAT_DATE_WITH_TIME_SECONDS)
 * без смещения времени у дат раньше 1970 года
 */
export const convertTimeForOldFormat = (time: string): string => {
  return new Date(time)
    ?.toLocaleDateString('ru-RU', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
    .replace(',', '');
};
